import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useI18n, LangSwitcher } from "./i18n";
import { ThemeSwitcher } from "./theme";
import { useStyles, useColors } from "./ui";
import { UserAvatar } from "./avatar";
import { destroyDatabase } from "./db";
import { IconTick } from "./icons";

declare global {
  interface Window {
    DataLab?: {
      getShellVersion(): string;
      isDarkMode(): boolean;
      setEmail(email: string): void;
      signInWithGoogle?(): void;
      shareText?(text: string): void;
      shareFile?(base64Data: string, mimeType: string, fileName: string): void;
    };
    __onGoogleSignIn?(
      result: { sessionId: string } | null,
      error?: string,
    ): void;
  }
}

interface Account {
  sessionId: string;
  userId: string;
  email: string;
  name: string;
  picture: string | null;
  role: string;
  isAdmin: boolean;
  hospitalIds: string[];
  unitIds: string[];
  subordinateUnitIds: string[];
}

interface AuthContextValue {
  user: Account | null;
  accounts: Account[];
  loading: boolean;
  authError: string | null;
  setAuthError: (msg: string | null) => void;
  clearAuthError: () => void;
  switchAccount: (sessionId: string) => void;
  addAccount: () => void;
  removeAccount: (sessionId: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const API_URL = import.meta.env.VITE_API_URL || "";
const STORAGE_KEY = "biostream-accounts";

function triggerGoogleSignIn(
  onSuccess: (sessionId: string) => void,
  onError: (msg: string) => void,
) {
  if (window.DataLab?.signInWithGoogle) {
    window.__onGoogleSignIn = (result, error) => {
      window.__onGoogleSignIn = undefined;
      if (result?.sessionId) onSuccess(result.sessionId);
      else onError(error || "Sign-in cancelled");
    };
    window.DataLab.signInWithGoogle();
  } else {
    window.location.href = `${API_URL}/login/google`;
  }
}

interface StoredAccounts {
  active: string | null;
  sessions: string[];
}

function getStoredAccounts(): StoredAccounts {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  // Migration from old single-session storage
  const oldSession = localStorage.getItem("biostream-session");
  if (oldSession) {
    localStorage.removeItem("biostream-session");
    return { active: oldSession, sessions: [oldSession] };
  }
  return { active: null, sessions: [] };
}

function saveStoredAccounts(data: StoredAccounts) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const clearAuthError = useCallback(() => setAuthError(null), []);

  // Load and validate accounts on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get("error");
    if (errorParam) {
      setAuthError(errorParam);
      window.history.replaceState({}, "", window.location.pathname);
    }
    const newSessionParam = params.get("session");

    let stored = getStoredAccounts();

    // Handle new session from OAuth callback
    if (newSessionParam) {
      if (!stored.sessions.includes(newSessionParam)) {
        stored.sessions.push(newSessionParam);
      }
      stored.active = newSessionParam;
      saveStoredAccounts(stored);
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (stored.sessions.length === 0) {
      setLoading(false);
      return;
    }

    // Validate all sessions in parallel
    // Track which sessions the server explicitly rejected (4xx) vs network errors
    const rejected = new Set<string>();
    Promise.all(
      stored.sessions.map(async (sessionId) => {
        try {
          const res = await fetch(`${API_URL}/api/me?session=${sessionId}`);
          if (!res.ok) {
            if (res.status >= 400 && res.status < 500) rejected.add(sessionId);
            return null;
          }
          const data = await res.json();
          return {
            sessionId,
            userId: data.id,
            email: data.email,
            name: data.name || data.email,
            picture: data.picture || null,
            role: data.role || "unauthorized",
            isAdmin: data.role === "admin",
            hospitalIds: data.hospitalIds ?? [],
            unitIds: data.unitIds ?? [],
            subordinateUnitIds: data.subordinateUnitIds ?? [],
          } as Account;
        } catch {
          // Network error — keep session, don't treat as invalid
          return null;
        }
      }),
    ).then((results) => {
      const validAccounts = results.filter((a): a is Account => a !== null);
      const validSessionIds = validAccounts.map((a) => a.sessionId);

      // Only remove sessions explicitly rejected by the server (4xx).
      // Keep sessions that failed due to network errors so they survive offline/flaky connections.
      const keptSessionIds = stored.sessions.filter(
        (s) => validSessionIds.includes(s) || !rejected.has(s),
      );
      const newStored: StoredAccounts = {
        sessions: keptSessionIds,
        active: keptSessionIds.includes(stored.active!)
          ? stored.active
          : keptSessionIds[0] || null,
      };
      saveStoredAccounts(newStored);

      setAccounts(validAccounts);
      setActiveSessionId(newStored.active);
      setLoading(false);
    });
  }, []);

  const switchAccount = useCallback(async (sessionId: string) => {
    const stored = getStoredAccounts();
    if (stored.sessions.includes(sessionId)) {
      stored.active = sessionId;
      saveStoredAccounts(stored);
      setActiveSessionId(sessionId);
      // Reload to reset all state and init new user's database
      window.location.reload();
    }
  }, []);

  const addAccount = useCallback(() => {
    triggerGoogleSignIn(
      (sessionId) => {
        const stored = getStoredAccounts();
        if (!stored.sessions.includes(sessionId)) {
          stored.sessions.push(sessionId);
        }
        stored.active = sessionId;
        saveStoredAccounts(stored);
        window.location.reload();
      },
      () => {}, // silently ignore cancel/error on add-account
    );
  }, []);

  const removeAccount = useCallback(async (sessionId: string) => {
    const stored = getStoredAccounts();
    const wasActive = stored.active === sessionId;
    stored.sessions = stored.sessions.filter((s) => s !== sessionId);
    if (wasActive) {
      // Destroy the database for the logged-out user
      await destroyDatabase();
      stored.active = stored.sessions[0] || null;
    }
    saveStoredAccounts(stored);
    setAccounts((prev) => prev.filter((a) => a.sessionId !== sessionId));

    if (wasActive) {
      setActiveSessionId(stored.active);
      if (stored.active) {
        // Switching to another account - reload to init their database
        window.location.reload();
      }
    }
  }, []);

  const logout = useCallback(() => {
    if (activeSessionId) {
      removeAccount(activeSessionId);
    }
  }, [activeSessionId, removeAccount]);

  const user = accounts.find((a) => a.sessionId === activeSessionId) || null;

  useEffect(() => {
    if (user?.email) {
      window.DataLab?.setEmail(user.email);
    }
  }, [user?.email]);

  return (
    <AuthContext.Provider
      value={{
        user,
        accounts,
        loading,
        authError,
        setAuthError,
        clearAuthError,
        switchAccount,
        addAccount,
        removeAccount,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** True if the user has any valid role (not unauthorized). */
export function hasAnyAccess(user: Account | null): boolean {
  if (!user) return false;
  return !!user.role && user.role !== "unauthorized";
}

const isSandbox = window.location.hostname.includes("sandbox");
const isDev =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

export function LoginPage() {
  const { t } = useI18n();
  const styles = useStyles();
  const colors = useColors();
  const { authError, setAuthError, clearAuthError } = useAuth();
  const [demoAvailable, setDemoAvailable] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/login/demo-available`)
      .then((r) => r.json())
      .then((d) => setDemoAvailable(d.available))
      .catch(() => {});
  }, []);

  const loginDemo = async () => {
    const res = await fetch(`${API_URL}/login/demo`, { method: "POST" });
    const { sessions } = await res.json();

    const stored = getStoredAccounts();
    for (const sessionId of Object.values(sessions) as string[]) {
      if (!stored.sessions.includes(sessionId)) {
        stored.sessions.push(sessionId);
      }
    }
    stored.active = sessions.admin;
    saveStoredAccounts(stored);
    window.location.reload();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: colors.bg,
        color: colors.text,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "12px",
          right: "12px",
          display: "flex",
          gap: "8px",
          alignItems: "center",
        }}
      >
        <ThemeSwitcher />
        <LangSwitcher />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          minWidth: "240px",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <h2 style={{ margin: 0, fontWeight: 600, color: colors.text }}>
          DataLab
        </h2>
        {(isSandbox || isDev) && (
          <div
            style={{
              background: isDev ? "#8b5cf6" : "#f59e0b",
              color: "#fff",
              padding: "12px 16px",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "0.9rem",
              textAlign: "center",
            }}
          >
            {isDev ? "DEV" : "SANDBOX"} —{" "}
            {t(isDev ? "auth.devWarning" : "auth.sandboxWarning")}
          </div>
        )}
        {authError && (
          <div
            style={{
              background: "#dc2626",
              color: "#fff",
              padding: "12px 16px",
              borderRadius: "8px",
              fontSize: "0.9rem",
              textAlign: "center",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: "4px" }}>
              {t("auth.loginError")}
            </div>
            <div style={{ fontSize: "0.8rem", opacity: 0.9 }}>{authError}</div>
          </div>
        )}
        <button
          style={{ ...styles.btnPrimary, textAlign: "center" }}
          onClick={() => {
            clearAuthError();
            triggerGoogleSignIn(
              (sessionId) => {
                const stored = getStoredAccounts();
                if (!stored.sessions.includes(sessionId)) {
                  stored.sessions.push(sessionId);
                }
                stored.active = sessionId;
                saveStoredAccounts(stored);
                window.location.reload();
              },
              (msg) => setAuthError(msg),
            );
          }}
        >
          {authError ? t("auth.tryAgain") : t("auth.signInWithGoogle")}
        </button>
        {demoAvailable && (
          <button onClick={loginDemo} style={styles.btn}>
            {t("auth.demo")}
          </button>
        )}
      </div>
    </div>
  );
}

// Account switcher component for navbar
export function AccountSwitcher() {
  const { user, accounts, switchAccount, addAccount, removeAccount } =
    useAuth();
  const { t } = useI18n();
  const colors = useColors();
  const styles = useStyles();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          ...styles.btn,
          fontSize: "0.75rem",
          padding: "2px 8px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <UserAvatar userId={user.userId} picture={user.picture} name={user.name} size={20} />
        <span>
          {user.name.includes("@") ? user.name.split("@")[0] : user.name}
        </span>
        <span style={{ fontSize: "0.6rem" }}>▼</span>
      </button>

      {open && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 99 }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              marginTop: "4px",
              background: colors.bg,
              border: `1px solid ${colors.border}`,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 100,
              minWidth: "220px",
            }}
          >
            {accounts.map((account) => (
              <div
                key={account.sessionId}
                style={{
                  padding: "8px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderBottom: `1px solid ${colors.border}`,
                  background:
                    account.sessionId === user.sessionId
                      ? colors.bgAlt
                      : "transparent",
                }}
              >
                <UserAvatar
                  userId={account.userId}
                  picture={account.picture}
                  name={account.name}
                  size={28}
                />
                <div
                  style={{ flex: 1, cursor: "pointer" }}
                  onClick={() => {
                    if (account.sessionId !== user.sessionId) {
                      switchAccount(account.sessionId);
                    }
                    setOpen(false);
                  }}
                >
                  <div style={{ fontSize: "0.875rem", color: colors.text }}>
                    {account.name}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: colors.textMuted }}>
                    {account.email}
                  </div>
                </div>
                {account.sessionId === user.sessionId && (
                  <span style={{ fontSize: "0.7rem", color: colors.success }}>
                    {IconTick}
                  </span>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAccount(account.sessionId);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: colors.danger,
                    cursor: "pointer",
                    fontSize: "1.25rem",
                    padding: "4px 8px",
                  }}
                  title={t("auth.removeAccount")}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                addAccount();
              }}
              style={{
                width: "100%",
                padding: "8px 12px",
                background: "none",
                border: "none",
                color: colors.primary,
                cursor: "pointer",
                textAlign: "left",
                fontSize: "0.875rem",
              }}
            >
              + {t("auth.addAccount")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
